import { useCallback, useMemo } from "react";

import { keyBy, uniq, uniqBy } from "lodash";

import { useUser } from "@components/user-context";
import { useCustomMetrics } from "@features/custom-metrics/hooks/use-custom-metrics";
import {
  type GetSalesReportV3Augmented_me_salesTableReportV3Augmented_actual_customGoals,
  makeBenchmarkRows,
  makeFirstTimeVsReturningRows,
  makeOverviewRows,
  makeTrafficRows,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";

import { useShowV1Forecasts } from "./use-show-v1-forecasts";

import { useCustomGoals, usePageConfiguration } from ".";

const resolve = (
  metric: SalesPageMetric,
  getMetricById: (id: string) => SalesPageMetric,
): SalesPageMetric[] => {
  if (metric?.requiredNBCalculatedMetrics) {
    return metric.requiredNBCalculatedMetrics.flatMap((e) =>
      resolve(getMetricById(e), getMetricById),
    );
  } else {
    return [metric];
  }
};

const resolveBaseMetrics = (
  m: SalesPageMetric,
  getMetricById: (id: string) => SalesPageMetric,
) => {
  const baseMetrics = m.baseMetrics?.length
    ? m.baseMetrics.flatMap((e) => e.requiredBaseMetrics)
    : m.requiredBaseMetrics;

  const calculatedMetrics = m?.requiredNBCalculatedMetrics
    ? resolve(m, getMetricById)
    : [];

  return uniq([
    ...baseMetrics,
    ...calculatedMetrics.flatMap((i) =>
      i ? i.requiredBaseMetrics : undefined,
    ),
  ]);
};

export const useAvailableMetrics = () => {
  const { customGoals } = useCustomGoals();
  const {
    accountingMode,
    attributionModel,
    attributionWindow,
    timeGranularity,
  } = usePageConfiguration();
  const showV1Forecasts = useShowV1Forecasts(
    timeGranularity,
    attributionModel,
    accountingMode,
  );
  const { metrics } = useAvailableMetricsWithArgs(
    { attributionModel, attributionWindow, accountingMode, timeGranularity },
    { showV1Forecasts, customGoals },
  );

  const { customMetricData } = useCustomMetrics(accountingMode);

  // Remove custom metrics that require base metrics unsupported
  // in selected attribution options / time granularity.
  // E.g ROAS Lift custom metric requires LTV ROAS,
  // which is not supported in hourly granularity
  const customMetricDataWithoutUnsupportedMetrics = useMemo(() => {
    if (customMetricData.length) {
      const metricsByIdIntermediate = keyBy(
        [...metrics, ...customMetricData],
        "id",
      );
      const getMetricByIdIntermediate = (id: string) =>
        metricsByIdIntermediate[id];

      return customMetricData.filter((customMetric) => {
        const resolvedMetrics = resolveBaseMetrics(
          customMetric,
          getMetricByIdIntermediate,
        );

        return !resolvedMetrics.includes(undefined);
      });
    }
    return customMetricData;
  }, [customMetricData, metrics]);

  const allMetrics = useMemo(() => {
    if (customMetricDataWithoutUnsupportedMetrics.length) {
      return [...metrics, ...customMetricDataWithoutUnsupportedMetrics];
    }
    return metrics;
  }, [customMetricDataWithoutUnsupportedMetrics, metrics]);

  const metricsById = useMemo(() => {
    return keyBy(allMetrics, "id");
  }, [allMetrics]);

  const getMetricById = useCallback(
    (id: string) => metricsById[id],
    [metricsById],
  );

  return {
    metrics: allMetrics,
    nbOnlyMetrics: metrics,
    customMetrics: customMetricDataWithoutUnsupportedMetrics,
    getMetricById,
  };
};

export const useAvailableMetricsWithArgs = (
  config: {
    attributionModel: string;
    attributionWindow: IMCAttributionWindowDays;
    accountingMode: INBAccountingMode;
    timeGranularity: ITimeGranularityV2;
  },
  options: {
    showV1Forecasts?: boolean | null;
    customGoals?: GetSalesReportV3Augmented_me_salesTableReportV3Augmented_actual_customGoals[];
    hideBenchmarks?: boolean;
    noAttributionWindowTransparency?: boolean;
  } = {},
) => {
  const { showV1Forecasts = false, customGoals = [], hideBenchmarks } = options;
  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
  } = config;
  const { user } = useUser();

  const defaultShowV1Forecasts = useShowV1Forecasts(
    timeGranularity,
    attributionModel,
    accountingMode,
  );
  const metrics = useMemo(() => {
    const canShowV1Forecasts = defaultShowV1Forecasts && !!showV1Forecasts;

    const metrics = [
      ...makeOverviewRows(
        attributionModel,
        attributionWindow,
        canShowV1Forecasts,
        accountingMode,
        customGoals,
        timeGranularity,
        // @ts-ignore
        user,
      ),
      ...makeTrafficRows(
        attributionModel,
        attributionWindow,
        accountingMode,
        // @ts-ignore
        user,
        timeGranularity,
      ),
      ...makeFirstTimeVsReturningRows(
        attributionModel,
        attributionWindow,
        accountingMode,
        timeGranularity,
        user.attributionMethodEnum,
      ),
      ...(hideBenchmarks
        ? []
        : makeBenchmarkRows(attributionModel, attributionWindow)),
    ].map((metric) => {
      if (options.noAttributionWindowTransparency) {
        return metric;
      }
      if (metric.metadata?.salesTableMetricId) {
        return {
          ...metric,
          /* This is a temp solution to keep the metric ids the same between different attribution
             windows */
          id: metric.metadata.salesTableMetricId,
        };
      }
      return metric;
    });

    return { metrics: uniqBy(metrics, "id") };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountingMode,
    attributionModel,
    attributionWindow,
    customGoals,
    defaultShowV1Forecasts,
    showV1Forecasts,
    timeGranularity,
    user.displayUserId,
  ]);
  return metrics;
};
